import beerIcon from "../assets/page_logo.png";
import imageIcon from "../assets/image.png";
import { useNavigate } from "react-router-dom";
import React, { useState, useRef, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import { businessApi } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { AGREEMENT_VERSION } from "../constants/agreement";

const resolveLoggedInEmail = (user, idToken) => {
  const profileEmail = (user?.email || user?.username || "").trim();
  if (profileEmail.includes("@")) {
    return profileEmail;
  }

  if (!idToken) {
    return profileEmail;
  }

  try {
    const payload = JSON.parse(atob(idToken.split(".")[1]));
    return (payload.email || payload["cognito:username"] || profileEmail).trim();
  } catch {
    return profileEmail;
  }
};

function CreateProfile() {
  const [businessName, setBusinessName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("+1");
  const [website, setWebsite] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [businessDescription, setBusinessDescription] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    phone: "",
    website: "",
  });
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { user, tokens, loading: authLoading } = useAuth();
  // Render nothing until the server has answered whether this partner has accepted. The
  // guard below is async, so without this the entire form painted while the GET was still
  // in flight and was then yanked away by the redirect — a partner signing back in without
  // an agreement saw the business form flash on every visit. /partner-agreement has held
  // the same gate since #28; this screen was missing its half of it.
  const [isChecking, setIsChecking] = useState(true);

  const userEmail = useMemo(
    () =>
      resolveLoggedInEmail(
        user,
        tokens?.id_token || localStorage.getItem("id_token"),
      ),
    [user, tokens?.id_token],
  );

  useEffect(() => {
    if (authLoading) return;

    const accessToken = localStorage.getItem("access_token");
    if (!accessToken) {
      toast.error("Please log in to create your business profile.", {
        position: "top-right",
        autoClose: 3000,
      });
      navigate("/admin-login");
      return;
    }

    // The partner agreement is a PREREQUISITE for this screen. It is confirmed against the
    // SERVER record now, not localStorage: a returning partner (new device, cleared storage)
    // who already accepted must pass, and one who has not is sent to accept first — before
    // anything is typed, so no work is lost. handleSave is still gated server-side
    // (POST /api/business -> AGREEMENT_NOT_ACCEPTED) as defense-in-depth. (#28, #29)
    let cancelled = false;
    (async () => {
      try {
        const status = await businessApi.getAgreementAcceptance();
        if (!cancelled && !status?.accepted) {
          // Leave isChecking true: the form must stay unrendered right through the
          // redirect, or the flash reappears in the gap before the route changes.
          navigate("/partner-agreement", { replace: true });
          return;
        }
      } catch (_) {
        // A transient read failure must not strand a partner who has accepted; let the
        // screen render and rely on the server-side create gate as the backstop.
      }
      if (!cancelled) {
        setIsChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, navigate]);

  // Validation functions
  const validatePhone = (phone) => {
    if (!phone) return "Phone number is required";
    // Allow phone numbers with +, digits, spaces, dashes, parentheses
    // Examples: +1 234 567 8900, (123) 456-7890, 123-456-7890, +1234567890
    const phoneRegex =
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(phone.trim())) {
      return "Please enter a valid phone number";
    }
    return "";
  };

  const validateWebsite = (website) => {
    if (!website) return "Website is required";
    // Require http:// or https:// protocol
    // Examples: https://example.com, http://example.com
    const websiteRegex =
      /^https?:\/\/([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
    if (!websiteRegex.test(website.trim())) {
      return "Please enter a valid website URL";
    }
    return "";
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setAttemptedSubmit(true);

    // Validate phone and website
    const phoneError = validatePhone(phoneNumber);
    const websiteError = validateWebsite(website);

    setValidationErrors({
      phone: phoneError,
      website: websiteError,
    });

    let message = "";
    if (!userEmail) message = "Unable to determine your account email. Please log in again.";
    else if (!businessName) message = "Business name is required";
    else if (!ownerName) message = "Owner name is required";
    else if (!phoneNumber) message = "Phone number is required";
    else if (phoneError) message = phoneError;
    else if (!website) message = "Website is required";
    else if (websiteError) message = websiteError;
    else if (!companyAddress) message = "Company address is required";
    else if (!businessDescription) message = "Business description is required";

    if (message) {
      toast.error(message, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Acceptance was recorded server-side at accept time and confirmed by the mount guard;
    // POST /api/business enforces it as well. We only need the version here for the
    // business-profile flag written after creation.
    const agreementVersion = AGREEMENT_VERSION.replace(/^v/i, "").trim();

    // Save has no disabled attribute, so a double-click issued two POSTs — and the server
    // generates a fresh business_id per request, so both succeeded (AC-PARTNER-E8).
    if (isSubmitting) {
      return;
    }
    setIsSubmitting(true);

    try {
      // If logoPreview exists and is a base64 data URL, send it. Otherwise send empty string.
      // The API will automatically upload base64 data URLs to S3 and store the S3 URL.
      const logoURLToSend = logoPreview || "";

      const response = await businessApi.createProfile({
        name: businessName,
        owner_name: ownerName,
        email: userEmail,
        phone: phoneNumber,
        website: website,
        address: companyAddress,
        description: businessDescription,
        logoURL: logoURLToSend,
        stripeVendorId: "",
      });

      // If we reach here, the API call was successful (axios throws on errors)
      const createdBusinessId = response?.business_id || response?.PK;

      if (!response || (!createdBusinessId && !response?.name)) {
        toast.error("Failed to create profile. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      // The agreement itself was validated before the business was created; only the
      // server-generated id is new information here.
      if (!createdBusinessId) {
        toast.error(
          "Unable to submit partner agreement (missing business_id).",
          {
            position: "top-right",
            autoClose: 4000,
          },
        );
        return;
      }

      await businessApi.setPartnerAgreement({
        business_id: createdBusinessId,
        agreement_version: agreementVersion,
        agreement: true,
      });

      toast.success("Profile created and agreement submitted!", {
        position: "top-right",
        autoClose: 3000,
      });
      navigate("/admin-dashboard/update-payment");
    } catch (err) {
      console.error("🚨 [Create Profile] Error:", err);

      // Extract error message from different possible error structures
      const errorMessage =
        err?.error ||
        err?.message ||
        (typeof err === "string" ? err : null) ||
        "Failed to create profile. Please try again.";

      // The server refused the create because no agreement acceptance is on record — a
      // backstop; the mount guard normally prevents reaching here. Route them to accept it
      // rather than showing a generic failure. (#29)
      if (err?.code === "AGREEMENT_NOT_ACCEPTED") {
        toast.error("Please accept the partner agreement to continue.", {
          position: "top-right",
          autoClose: 4000,
        });
        navigate("/partner-agreement");
        return;
      }

      // A 401 that reaches here means the refresh failed too, so authClient has
      // already cleared the session and AuthContext is navigating to sign-in.
      // This used to sniff the message for five different substrings, which was
      // the only screen that happened to catch "Token has expired" — the others
      // matched on different words and missed it.
      if (err?.isAuthError) {
        toast.error("Your session has expired. Please log in again.", {
          position: "top-right",
          autoClose: 4000,
        });
      } else {
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 4000,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Covers the auth-loading wait, the token check and the acceptance round-trip: the form
  // paints once, when we know the partner is allowed to be here.
  if (isChecking) {
    return null;
  }

  return (
    <div
      style={{
        backgroundColor: "#F9F4EA",
        width: "100%",
        // border-box: width:100% plus 20px of side padding was 40px wider than the viewport.
        boxSizing: "border-box",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        padding: "40px 20px",
      }}
    >
      {/* Main Content */}
      <div
        style={{
          maxWidth: "1200px",
          width: "100%",
          margin: "0 auto",
          display: "flex",
          // Same stacked-on-a-phone shape as the other onboarding screens. AC-MOBILE-F2.
          flexWrap: "wrap",
          gap: "clamp(24px, 6vw, 80px)",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Left: Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <img
            src={beerIcon}
            alt="beer icon"
            style={{ width: "min(400px, 60vw)", height: "auto", cursor: "pointer" }}
            onClick={() => navigate("/admin-login")}
          />
        </div>

        {/* Right: Form */}
        <div
          style={{
            flex: 1,
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
            padding: "40px",
          }}
        >
          <h2
            style={{
              textAlign: "left",
              marginTop: "0px",
              marginBottom: "30px",
              fontSize: "clamp(24px, 6vw, 40px)",
              fontWeight: "bold",
              color: "#303030",
            }}
          >
            Create Account
          </h2>

          {/* Row of 3 textfields: Business Name, Owner Name, Phone Number */}
          <div
            style={{
              display: "flex",
              // Wrap: three fields side by side gave this column a 616px intrinsic width,
              // which the whole page then had to be as wide as. AC-MOBILE-F1.
              flexWrap: "wrap",
              gap: "20px",
              marginBottom: "20px",
            }}
          >
            <div
              style={{
                flex: "1 1 220px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  marginBottom: "6px",
                }}
              >
                Business Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  border:
                    businessName === "" && attemptedSubmit
                      ? "1px solid red"
                      : "1px solid #ccc",
                  boxSizing: "border-box",
                }}
              />
              {businessName === "" && attemptedSubmit && (
                <span
                  style={{
                    color: "red",
                    fontStyle: "italic",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  Business Name is required
                </span>
              )}
            </div>

            <div
              style={{
                flex: "1 1 220px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  marginBottom: "6px",
                }}
              >
                Owner Name <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  border:
                    ownerName === "" && attemptedSubmit
                      ? "1px solid red"
                      : "1px solid #ccc",
                  boxSizing: "border-box",
                }}
              />
              {ownerName === "" && attemptedSubmit && (
                <span
                  style={{
                    color: "red",
                    fontStyle: "italic",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  Owner Name is required
                </span>
              )}
            </div>

            <div
              style={{
                flex: "1 1 220px",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <label
                style={{
                  fontSize: "14px",
                  color: "black",
                  marginBottom: "6px",
                }}
              >
                Phone Number <span style={{ color: "red" }}>*</span>
              </label>
              <input
                type="text"
                value={phoneNumber}
                onChange={(e) => {
                  setPhoneNumber(e.target.value);
                  // Clear error when user starts typing
                  if (validationErrors.phone) {
                    setValidationErrors({
                      ...validationErrors,
                      phone: "",
                    });
                  }
                }}
                onBlur={() => {
                  const error = validatePhone(phoneNumber);
                  setValidationErrors({
                    ...validationErrors,
                    phone: error,
                  });
                }}
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  border:
                    (phoneNumber === "" && attemptedSubmit) ||
                    validationErrors.phone
                      ? "1px solid red"
                      : "1px solid #ccc",
                  boxSizing: "border-box",
                }}
              />
              {(phoneNumber === "" && attemptedSubmit) ||
              validationErrors.phone ? (
                <span
                  style={{
                    color: "red",
                    fontStyle: "italic",
                    fontSize: "12px",
                    marginTop: "4px",
                  }}
                >
                  {phoneNumber === "" && attemptedSubmit
                    ? "Phone Number is required"
                    : validationErrors.phone}
                </span>
              ) : null}
            </div>
          </div>

          {/* Website */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                fontSize: "14px",
                color: "black",
                marginBottom: "6px",
                display: "block",
              }}
            >
              Website <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="url"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
                // Clear error when user starts typing
                if (validationErrors.website) {
                  setValidationErrors({
                    ...validationErrors,
                    website: "",
                  });
                }
              }}
              onBlur={() => {
                const error = validateWebsite(website);
                setValidationErrors({
                  ...validationErrors,
                  website: error,
                });
              }}
              placeholder="https://example.com"
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border:
                  (website === "" && attemptedSubmit) ||
                  validationErrors.website
                    ? "1px solid red"
                    : "1px solid #ccc",
                boxSizing: "border-box",
              }}
            />
            {(website === "" && attemptedSubmit) || validationErrors.website ? (
              <span
                style={{
                  color: "red",
                  fontStyle: "italic",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                {website === "" && attemptedSubmit
                  ? "Website is required"
                  : validationErrors.website}
              </span>
            ) : null}
          </div>

          {/* Company Address */}
          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                fontSize: "14px",
                color: "black",
                marginBottom: "6px",
                display: "block",
              }}
            >
              Company Address <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              value={companyAddress}
              onChange={(e) => setCompanyAddress(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border:
                  companyAddress === "" && attemptedSubmit
                    ? "1px solid red"
                    : "1px solid #ccc",
                boxSizing: "border-box",
              }}
            />
            {companyAddress === "" && attemptedSubmit && (
              <span
                style={{
                  color: "red",
                  fontStyle: "italic",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Company Address is required
              </span>
            )}
          </div>

          {/* Business Description */}
          <div style={{ marginBottom: "30px" }}>
            <label
              style={{
                fontSize: "14px",
                color: "black",
                marginBottom: "6px",
                display: "block",
              }}
            >
              Business Description <span style={{ color: "red" }}>*</span>
            </label>
            <textarea
              value={businessDescription}
              onChange={(e) => setBusinessDescription(e.target.value)}
              rows={4}
              style={{
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border:
                  businessDescription === "" && attemptedSubmit
                    ? "1px solid red"
                    : "1px solid #ccc",
                boxSizing: "border-box",
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
            {businessDescription === "" && attemptedSubmit && (
              <span
                style={{
                  color: "red",
                  fontStyle: "italic",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Business Description is required
              </span>
            )}
          </div>

          {/* Logo Upload */}
          <div
            style={{
              display: "flex",
              // Wrap rather than squeeze: unwrapped, the preview box was flex-shrunk to a
              // non-square sliver and the button's label broke across three lines inside a
              // pill radius. AC-MOBILE-F1.
              flexWrap: "wrap",
              alignItems: "center",
              gap: "20px",
              marginBottom: "30px",
            }}
          >
            <div
              style={{
                width: "200px",
                height: "200px",
                flexShrink: 0,
                backgroundColor: "rgba(222, 129, 14, 0.1)",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed #ccc",
                overflow: "hidden",
              }}
            >
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="logo preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <img
                  src={imageIcon}
                  alt="logo placeholder"
                  style={{ width: "40px", height: "40px", opacity: 0.6 }}
                />
              )}
            </div>
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleLogoUpload}
                accept="image/*"
                style={{ display: "none" }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  padding: "12px 24px",
                  borderRadius: "9999px",
                  whiteSpace: "nowrap",
                  border: "1px solid #DE810E",
                  cursor: "pointer",
                  background: "white",
                  fontWeight: "bold",
                  color: "#DE810E",
                  fontSize: "16px",
                  fontFamily: "Poppins, sans-serif",
                }}
              >
                Upload Company Logo
              </button>
            </div>
          </div>

          {/* Save Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              flexDirection: "column",
              gap: "10px",
            }}
          >
            <button
              onClick={handleSave}
              disabled={isSubmitting}
              style={{
                width: "120px",
                height: "50px",
                borderRadius: "30px",
                border: "none",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                background: isSubmitting ? "#C9C9C9" : "#DE810E",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
              }}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateProfile;
